import { ArrowRight, Shield, Award, TrendingUp, PieChart, Target, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEO from '../SEO';
import { pageSEO, faqSchemas } from '../../seoData';
import RelatedServices from '../RelatedServices';

const PortfolioManagement = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    { question: "What is PMS and how does it work?", answer: "Portfolio Management Service (PMS) is a professional investment service where experienced fund managers create and manage a customized stock portfolio on your behalf. Unlike mutual funds, PMS gives you direct ownership of shares. Our PMS registration is APRN00365." },
    { question: "What is the minimum investment for PMS?", answer: "The SEBI mandated minimum investment for PMS is Rs.50 Lakhs. This ensures that PMS services are available to investors with sufficient capital for meaningful portfolio diversification and professional management." },
    { question: "How are PMS fees structured?", answer: "PMS typically charges 2-2.5% of Assets Under Management (AUM) per year as management fees. Some PMS also have a profit-sharing component. We provide complete fee disclosure upfront with no hidden charges." },
    { question: "What returns can I expect from PMS?", answer: "Returns depend on market conditions, investment strategy, and time horizon. Historically, well-managed PMS portfolios have delivered 15-20% CAGR over 5+ year periods. However, past performance doesn't guarantee future returns. We focus on risk-adjusted returns and capital protection." },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.portfolioManagement} faqJsonLd={faqSchemas.portfolioManagement} />

      <section className="relative bg-gradient-to-br from-indigo-50 via-white to-violet-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="inline-block bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6">
              PMS | APRN00365
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Portfolio Management
              <span className="block bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
                Services (PMS)
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-3xl">
              Professional portfolio management for High Net Worth Individuals. Our SEBI-registered PMS 
              (APRN00365) offers customized investment strategies managed by experienced portfolio managers. 
              With Rs.500 Cr+ under management and 28+ years of market expertise, we deliver consistent 
              risk-adjusted returns through disciplined investment processes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-indigo-700 hover:to-violet-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl" data-testid="pms-cta">
                Explore PMS <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/services" className="border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div><div className="text-3xl font-bold text-indigo-600 mb-1">Rs.500Cr+</div><div className="text-gray-600 text-sm">Assets Under Management</div></div>
            <div><div className="text-3xl font-bold text-indigo-600 mb-1">APRN00365</div><div className="text-gray-600 text-sm">PMS Registration</div></div>
            <div><div className="text-3xl font-bold text-indigo-600 mb-1">Rs.50L</div><div className="text-gray-600 text-sm">Min Investment</div></div>
            <div><div className="text-3xl font-bold text-indigo-600 mb-1">28+ Years</div><div className="text-gray-600 text-sm">Market Experience</div></div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">Why Choose Our PMS?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <Shield className="h-7 w-7 text-white" />, title: "SEBI Registered", desc: "Fully regulated PMS with SEBI registration APRN00365. Complete transparency in holdings, transactions, and performance reporting." },
              { icon: <PieChart className="h-7 w-7 text-white" />, title: "Customized Portfolios", desc: "Unlike mutual funds, PMS creates personalized portfolios tailored to your specific goals, risk tolerance, and investment horizon." },
              { icon: <TrendingUp className="h-7 w-7 text-white" />, title: "Active Management", desc: "Experienced portfolio managers actively monitor and rebalance your portfolio to capture opportunities and manage risks in real-time." },
              { icon: <Award className="h-7 w-7 text-white" />, title: "Direct Ownership", desc: "You directly own the shares in your demat account. Full visibility of every stock, every transaction. No pooling of funds with other investors." },
              { icon: <Target className="h-7 w-7 text-white" />, title: "Risk Management", desc: "Sophisticated risk management with sector limits, stock-level stop-losses, and hedging strategies to protect your capital during market downturns." },
              { icon: <BarChart3 className="h-7 w-7 text-white" />, title: "Detailed Reporting", desc: "Monthly performance reports, real-time portfolio access, tax statements, and quarterly review meetings with your portfolio manager." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl flex items-center justify-center mb-5">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">PMS FAQs</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-gray-50 rounded-xl overflow-hidden">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex justify-between items-center">
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-2xl text-indigo-600 ml-4">{expandedFaq === i ? '−' : '+'}</span>
                </button>
                {expandedFaq === i && <div className="px-6 pb-5"><p className="text-gray-700 leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['equity', 'mutualFund', 'wealth', 'nri', 'calculator']} />

      <section className="py-16 bg-gradient-to-r from-indigo-600 to-violet-600">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Explore Portfolio Management Services</h2>
          <p className="text-lg mb-8 text-indigo-100">Get a personalized PMS strategy from our experienced portfolio managers. Free consultation.</p>
          <Link to="/contact" className="bg-white text-indigo-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all inline-flex items-center">
            Schedule Consultation <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PortfolioManagement;
